/**
 * Marketing Hub — Governance (flat file, no subdirectory imports)
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Search, FileText, PenTool, BarChart3, Globe, Rocket, Sparkles, TrendingUp } from 'lucide-react';
import R from '../../navigation/opsRoutes';
import { useGovernanceLang } from '../../contexts/GovernanceLangContext';

const BRAND_GREEN = '#064e3b';
const BRAND_GOLD = '#b48c2c';

const CARDS_STATIC = [
  { icon: Search,   path: R.marketingSeo,          badge: 'new',  ready: true  },
  { icon: FileText, path: R.editorialEngine,        badge: null,   ready: true  },
  { icon: PenTool,  path: R.marketingCreator,       badge: null,   ready: true  },
  { icon: BarChart3,path: R.marketingStudyBuilder,  badge: 'new',  ready: true  },
  { icon: TrendingUp,path:R.marketingAnalyticsTool, badge: null,   ready: true  },
  { icon: Sparkles, path: R.marketingContentStudio, badge: null,   ready: true  },
  { icon: Globe,    path: null,                     badge: 'soon', ready: false },
  { icon: Rocket,   path: null,                     badge: 'soon', ready: false },
];

const MKT_LABELS = {
  pt: {
    pageTitle: 'Marketing',
    pageSubtitle: 'SEO, conteúdo, landing pages, campanhas e growth.',
    open: 'Abrir →',
    new: 'NOVO', soon: 'EM BREVE',
    cards: [
      { title: 'SEO do Site',       description: 'Painel de SEO técnico: meta tags, sitemap, structured data, checklist e páginas estratégicas.' },
      { title: 'Editorial Engine',  description: 'Pipeline editorial completo: insights → drafts → publicação de estudos.' },
      { title: 'Creator Studio',    description: 'Gerar posts, narrativas editoriais e conteúdo de rede social.' },
      { title: 'Study Builder',     description: 'Construtor de estudos interativo: escolha competição, ano, prova e monte gráficos.' },
      { title: 'Analytics Tool',    description: 'Dashboards internos, métricas de produto e monitoramento.' },
      { title: 'Content Studio',    description: 'Criação de conteúdo com IA e templates.' },
      { title: 'Landing Pages',     description: 'Gerencie páginas públicas: Swim Vision, TrackVision, Reakt.' },
      { title: 'Campanhas',         description: 'Campanhas de aquisição, distribuição de conteúdo e growth.' },
    ],
  },
  en: {
    pageTitle: 'Marketing',
    pageSubtitle: 'SEO, content, landing pages, campaigns and growth.',
    open: 'Open →',
    new: 'NEW', soon: 'COMING SOON',
    cards: [
      { title: 'Site SEO',          description: 'Technical SEO dashboard: meta tags, sitemap, structured data, checklist and strategic pages.' },
      { title: 'Editorial Engine',  description: 'Full editorial pipeline: insights → drafts → publishing studies.' },
      { title: 'Creator Studio',    description: 'Generate posts, editorial narratives and social network content.' },
      { title: 'Study Builder',     description: 'Interactive study builder: choose competition, year, event and build charts.' },
      { title: 'Analytics Tool',    description: 'Internal dashboards, product metrics and monitoring.' },
      { title: 'Content Studio',    description: 'AI-powered content creation with templates.' },
      { title: 'Landing Pages',     description: 'Manage public pages: Swim Vision, TrackVision, Reakt.' },
      { title: 'Campaigns',         description: 'Acquisition campaigns, content distribution and growth.' },
    ],
  },
};

const GovernanceMarketingPage = () => {
  const { lang } = useGovernanceLang();
  const t = MKT_LABELS[lang] || MKT_LABELS.pt;
  const cards = CARDS_STATIC.map((s, i) => ({ ...s, ...t.cards[i] }));

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <img src="/vukz-logo.png" alt="Vukz" className="w-11 h-11 rounded-xl object-contain bg-gray-50 border border-gray-200 p-0.5" onError={(e) => { e.target.style.display = 'none'; }} />
        <div>
          <h1 className="text-gray-900 text-2xl font-extrabold">{t.pageTitle}</h1>
          <p className="text-gray-500 mt-1">{t.pageSubtitle}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {cards.map((c) => {
          const Wrapper = c.ready && c.path ? Link : 'div';
          const props = c.ready && c.path ? { to: c.path } : {};
          const badgeText = c.badge === 'new' ? t.new : c.badge === 'soon' ? t.soon : null;
          return (
            <Wrapper key={c.title} {...props}
              className={`block bg-white rounded-xl border border-gray-200 p-6 transition-all duration-200 ${c.ready ? 'hover:shadow-md cursor-pointer' : 'opacity-60 cursor-default'}`}>
              <div className="flex items-center gap-2 mb-2">
                <c.icon className="w-5 h-5" style={{ color: BRAND_GREEN }} />
                <h2 className="text-lg font-bold" style={{ color: BRAND_GREEN }}>{c.title}</h2>
                {badgeText && <span className="px-1.5 py-0.5 text-[10px] font-bold rounded" style={{ backgroundColor: c.ready ? BRAND_GOLD : '#9ca3af', color: '#fff' }}>{badgeText}</span>}
              </div>
              <p className="text-sm text-gray-500">{c.description}</p>
              {c.ready && c.path && <span className="inline-block mt-4 text-sm font-semibold" style={{ color: BRAND_GOLD }}>{t.open}</span>}
            </Wrapper>
          );
        })}
      </div>
    </div>
  );
};

export default GovernanceMarketingPage;
